import { i18n } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnalyticsFunnelCard } from '../components/analytics-funnel-card.js';
import { buildAgentPromptFromKey } from '../components/analytics-install-option.js';
import { AnalyticsUsageCard } from '../components/analytics-usage-card.js';
import { CountUp } from '../components/count-up.js';
import { Drawer, DrawerCloseButton, useDrawer } from '../components/drawer.js';
import { Mag } from '../components/mag.js';
import { PasswordInput } from '../components/password-input.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import { buildInstallTag, SDK_INGEST_URL } from '../lib/analytics-snippet.js';
import {
  type AnalyticsActiveSession,
  type AnalyticsAudience,
  type AnalyticsEnvelope,
  type AnalyticsEventRow,
  type AnalyticsPathClickCount,
  type AnalyticsProject,
  type AnalyticsSummary,
  type AnalyticsTopFacet,
  type AnalyticsVisitsByDay,
  type ConversionForwardConfig,
  type ConversionForwardLogEntry,
  type ConversionForwardsEnvelope,
  type ConversionPlatform,
  capiCredentialsComplete,
  createAnalyticsProject,
  fetchConversionForwards,
  type fetchMe,
  fetchMetaPixels,
  getAnalyticsServerStatus,
  hasCapiAccessToken,
  hasCapiPixelId,
  type MetaPixelRefusal,
  type MetaPixelsEnvelope,
  revealAnalyticsProjectWriteKey,
  setAnalyticsProjectWriteKey,
  setConversionForward,
  updateAnalyticsProject,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import {
  formatDate,
  formatDateTime,
  formatNumber,
  formatRelativeTimeCompact,
} from '../lib/format.js';
import {
  activeAnalyticsQuery,
  analyticsQuery,
  brandsQuery,
  meQuery,
  visitorEventsQuery,
} from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// /brand/$slug/analytics — single-brand analytics setup + findings.
// Each brand owns its tracking; the auto-provisioned tracker created
// at onboarding (see ensureBrandTracker after scrape-url) is this
// page's primary subject. Multi-tracker brands (separate site, sub-
// domain analyzed independently) are a niche supported behind the
// ↘ advanced disclosure.
//
// Per the product spec: "mediated through Mag — not a dashboard". We
// don't render heatmaps, scroll funnels, or events feeds. Mag reads
// the data; the founder reads Mag.

export const Route = createFileRoute('/brand/$slug/analytics')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    const envelope = await queryClient.ensureQueryData(analyticsQuery(brand.id));
    return { me, envelope, brand };
  },
  loader: ({ context }) =>
    context as {
      me: Awaited<ReturnType<typeof fetchMe>>;
      envelope: AnalyticsEnvelope | null;
      brand: { id: string; name: string; slug: string };
    },
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { envelope: initialEnvelope, brand } = Route.useLoaderData();
  useBrandTitle(brand.slug, ['analytics']);

  // Subscribe to the analytics cache so the page flips from install-mode
  // to live-mode automatically the moment the founder pastes the snippet
  // and pings their site — no refresh required. Loader data primes the
  // first render; refetchInterval drives ongoing polling. Cadence is
  // tight pre-first-event (when the founder is most likely to be
  // staring at this page) and relaxes once at least one tracker is live.
  const { data: envelope } = useQuery({
    ...analyticsQuery(brand.id),
    initialData: initialEnvelope,
    refetchInterval: (query) => {
      const env = query.state.data as AnalyticsEnvelope | null | undefined;
      const anyLive = env?.projects?.some((p) => p.firstEventAt) ?? false;
      return anyLive ? 10_000 : 3_000;
    },
    // Stop the timer when the tab is hidden — no point polling a page
    // no one is looking at; reactivates on focus.
    refetchIntervalInBackground: false,
  });

  // Oldest-first so the auto-provisioned tracker (from the onboarding
  // URL-scrape) is consistently slot 0 — the brand's primary tracker.
  // Any "add another" trackers stack behind it in the advanced section.
  const trackers = useMemo(() => {
    const list = envelope?.projects ?? [];
    return [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }, [envelope]);

  const [selectedId, setSelectedId] = useState(trackers[0]?.id ?? '');
  const selected = trackers.find((t) => t.id === selectedId) ?? trackers[0];

  // /api/analytics returns the full envelope scoped to the brand;
  // summary / events / pathClicks attach to the *first* project today,
  // which is fine because the typical brand has one tracker. When the
  // founder switches in the advanced disclosure we still show those
  // rollups against the same envelope until the API takes a project
  // filter — acceptable lossy fallback (the advanced case is rare).
  const summary = envelope?.summary ?? null;
  const recentEvents = useMemo(() => {
    const list = envelope?.events ?? [];
    if (!selected) return list;
    return list.filter((e) => e.projectId === selected.id);
  }, [envelope, selected]);
  const pathClicks = envelope?.pathClicks ?? [];
  const visitsByDay = envelope?.visitsByDay ?? [];
  const audience = envelope?.audience ?? null;

  // "Who's on the site right now" — separate from the main envelope so
  // the heavier rollups don't have to round-trip every 15s. Only worth
  // polling once we have a live tracker.
  const anyLive = trackers.some((t) => t.firstEventAt);
  const { data: activeEnvelope } = useQuery({
    ...activeAnalyticsQuery(brand.id),
    enabled: anyLive,
    refetchInterval: anyLive ? 15_000 : false,
    refetchIntervalInBackground: false,
  });
  const activeSessions = activeEnvelope?.activeSessions ?? [];
  const activeWindowMinutes = activeEnvelope?.windowMinutes ?? 5;

  // Drilldown state — clicking an active-session row opens a slide-
  // over with that session's event tail. Lifted here so the slide-
  // over can mount as a sibling of <section> (right-edge, full-height)
  // rather than nested inside a card.
  const [drilldownSession, setDrilldownSession] = useState<AnalyticsActiveSession | null>(null);

  const refresh = async () => {
    await refreshQueries(queryClient, ['analytics'], ['analytics-active']);
    await router.invalidate();
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="analytics" />
      <section className="mx-auto max-w-[820px] px-4 pb-16 pt-8 sm:px-7">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} {brand.name.toLowerCase()} · <Trans>analytics</Trans>
        </p>
        <h1 className="mt-2 font-display text-[28px] sm:text-[34px] font-black leading-[1.05] tracking-[-0.04em]">
          <Trans>mag's eye on {brand.name.toLowerCase()}.</Trans>
        </h1>
        <p className="mt-3 max-w-[680px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
          <Trans>
            one tag on your site, mag reads what your visitors do. install + live view for this
            brand specifically — every brand has its own tracking, isolated from your other brands.
          </Trans>
        </p>

        {/* Readings store (ClickHouse) had a transient blip and the
            api degraded the data half to empty — the tracker list is
            still real. Tell the founder it's a reconnect, not "no data",
            so an empty live view doesn't read as a lost tracker. */}
        {envelope?.degraded ? (
          <div className="mt-5 rounded-[10px] border border-[var(--plum)]/20 bg-[var(--plum)]/[0.04] px-4 py-3 text-[13px] leading-[1.45] text-[var(--ink)]/75">
            <Trans>
              mag's readings store hiccupped for a sec — your tracker's fine, the live numbers are
              just catching their breath. refresh in a moment.
            </Trans>
          </div>
        ) : null}

        {/* s162.37 — the founder lands on /analytics to read what
            mag is seeing, not to check their plan usage. tracker
            view goes first (live data, install state, audience);
            usage gauge sits below as a quiet status row. */}
        {selected ? (
          <TrackerView
            tracker={selected}
            brandId={brand.id}
            summary={summary}
            events={recentEvents}
            pathClicks={pathClicks}
            visitsByDay={visitsByDay}
            audience={audience}
            activeSessions={activeSessions}
            activeWindowMinutes={activeWindowMinutes}
            onOpenSession={setDrilldownSession}
            onChanged={refresh}
          />
        ) : (
          <SetUpTrackingPrompt brandId={brand.id} onCreated={refresh} />
        )}

        {/* Org-wide events-this-month gauge. Falls back silently if
            the api 500s — analytics is the primary purpose of this
            page, not billing. */}
        <div className="mt-6">
          <AnalyticsUsageCard />
        </div>

        <AdvancedDisclosure
          trackers={trackers}
          selectedId={selected?.id ?? null}
          onSelect={setSelectedId}
          brandId={brand.id}
          onChanged={refresh}
          // Once events are flowing the install snippet + agent prompt
          // get tucked behind ↘ advanced — they're rarely needed past
          // first install, but stay reachable for reinstalls / new
          // subdomains without dominating the live view.
          tuckedInstall={selected?.firstEventAt ? selected : null}
        />
      </section>
      {drilldownSession ? (
        <SessionDrilldown
          brandId={brand.id}
          session={drilldownSession}
          onClose={() => setDrilldownSession(null)}
        />
      ) : null}
    </main>
  );
}

// ─── Single-tracker view (the brand's primary) ─────────────────────────

function TrackerView({
  tracker,
  brandId,
  summary,
  events,
  pathClicks,
  visitsByDay,
  audience,
  activeSessions,
  activeWindowMinutes,
  onOpenSession,
  onChanged,
}: {
  tracker: AnalyticsProject;
  brandId: string;
  summary: AnalyticsSummary | null;
  events: AnalyticsEventRow[];
  pathClicks: AnalyticsPathClickCount[];
  visitsByDay: AnalyticsVisitsByDay[];
  audience: AnalyticsAudience | null;
  activeSessions: AnalyticsActiveSession[];
  activeWindowMinutes: number;
  onOpenSession: (session: AnalyticsActiveSession) => void;
  onChanged: () => Promise<void>;
}) {
  const isLive = Boolean(tracker.firstEventAt);
  return (
    <div className="mt-6 flex flex-col gap-5">
      <TrackerStatusCard tracker={tracker} onChanged={onChanged} />
      {isLive ? (
        <>
          {/* Once events are flowing, the page becomes a "what is the
              SDK seeing" view. Install snippet + agent prompt are
              tucked into ↘ advanced — useful for reinstall, not for
              every page load. */}
          <ActiveNowCard
            sessions={activeSessions}
            windowMinutes={activeWindowMinutes}
            onOpenSession={onOpenSession}
          />
          <LiveSignalsCard summary={summary} firstEventAt={tracker.firstEventAt} />
          <AnalyticsFunnelCard brandId={brandId} />
          <VisitsByDayCard visitsByDay={visitsByDay} />
          <AudienceCard audience={audience} />
          <RecentPathsCard pathClicks={pathClicks} />
          <RecentEventsCard events={events} />
        </>
      ) : (
        <>
          {/* Pre-first-event: install-mode view. Snippet + agent prompt
              up top so the founder sees them before scrolling. */}
          <InstallPanel project={tracker} />
          <AgentPromptPanel project={tracker} />
        </>
      )}
      <AllowedHostsCard tracker={tracker} onChanged={onChanged} />
      <WriteKeyCard tracker={tracker} onChanged={onChanged} />
      <ConversionForwardingPanel project={tracker} />
    </div>
  );
}

// ─── Live view cards (shown once the tracker has received events) ─────

function LiveSignalsCard({
  summary,
  firstEventAt,
}: {
  summary: AnalyticsSummary | null;
  firstEventAt: string | null;
}) {
  const { t } = useLingui();
  if (!summary) {
    return (
      <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>live signals</Trans>
        </p>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>waiting for the rollup to catch up · refresh in a moment</Trans>
        </p>
      </article>
    );
  }
  // WHAT THE CONVERSIONS NUMBER IS MADE OF, rendered WITH the number
  // rather than as a footnote under the grid.
  //
  // The total counts every conversion-type event whatever it was named,
  // so `signup_started` — a click on a CTA on the marketing page — lands
  // in it exactly like `account_created`. Read alone it says "someone
  // converted"; on 2026-10-05 it was read as "someone signed up and
  // paid" when nobody had done either.
  const byName = summary.conversionsByName ?? [];
  const shown = byName.reduce((sum, c) => sum + c.count, 0);
  // The breakdown is capped server-side, so it can sum to less than the
  // total. Showing the parts without the remainder would let them
  // silently contradict the number they are there to explain.
  const remainder = Math.max(0, summary.conversions - shown);
  const conversionsCaveat =
    summary.conversions === 0 ? null : byName.length === 0 ? (
      // Non-zero total with no breakdown: an api build that predates the
      // field, or the breakdown query failing. Say so rather than leave
      // the bare number stating a conclusion on its own.
      <Trans>breakdown unavailable — this counts every conversion event</Trans>
    ) : (
      <>
        {byName.map((c) => (
          <span key={c.name} className="mr-2 inline-block whitespace-nowrap">
            <span className="font-semibold text-[var(--plum)]">
              {c.name === '' ? t`unnamed` : c.name}
            </span>{' '}
            {formatNumber(c.count)}
          </span>
        ))}
        {remainder > 0 ? (
          <span className="inline-block whitespace-nowrap text-[var(--ink)]/55">
            <Trans>+{formatNumber(remainder)} more</Trans>
          </span>
        ) : null}
      </>
    );

  const tiles: { label: string; value: number; caveat?: React.ReactNode }[] = [
    { label: t`pageviews`, value: summary.pageViews },
    { label: t`clicks`, value: summary.clicks },
    { label: t`unique visitors`, value: summary.uniqueVisitors },
    { label: t`conversions`, value: summary.conversions, caveat: conversionsCaveat },
  ];
  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>live signals · last {summary.windowDays}d</Trans>
        </p>
        {firstEventAt ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>first event · {formatDate(firstEventAt)}</Trans>
          </span>
        ) : null}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="rounded-xl border border-[var(--line)] bg-[var(--paper)] px-3 py-3"
          >
            <p className="font-display text-[24px] font-black leading-none tracking-[-0.02em] text-[var(--ink)]">
              {formatNumber(t.value)}
            </p>
            <p className="mt-1.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
              {t.label}
            </p>
            {t.caveat ? (
              <p className="mt-1.5 font-mono text-[10px] leading-[1.5] text-[var(--ink)]/70">
                {t.caveat}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </article>
  );
}

function ActiveNowCard({
  sessions,
  windowMinutes,
  onOpenSession,
}: {
  sessions: AnalyticsActiveSession[];
  windowMinutes: number;
  onOpenSession: (session: AnalyticsActiveSession) => void;
}) {
  const count = sessions.length;
  // Tick once a second so the "x ago" labels refresh between server
  // polls — feels alive even when no new data has arrived yet.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>on the site right now</Trans>
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>last {windowMinutes}m · auto-refresh</Trans>
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-3">
        <span className="font-display text-[34px] font-black leading-none tracking-[-0.03em] text-[var(--ink)] tabular-nums">
          <CountUp value={count} />
        </span>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] ${
            count > 0
              ? 'bg-[var(--spell)] text-[var(--ink)]'
              : 'border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]/55'
          }`}
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${
              count > 0 ? 'mag-pulse bg-[var(--pulse)]' : 'bg-[var(--ink)]/25'
            }`}
          />
          {plural(count, { one: 'session', other: 'sessions' })}
        </span>
      </div>
      {count === 0 ? (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>quiet — no one on the site in the last {windowMinutes} minutes.</Trans>
        </p>
      ) : (
        <ul className="mt-4 flex flex-col">
          {sessions.map((s) => (
            <li key={s.anonId}>
              <button
                type="button"
                onClick={() => onOpenSession(s)}
                className="group flex w-full items-baseline gap-3 border-b border-[var(--line)] py-2 text-left last:border-b-0 hover:bg-[var(--paper)]"
              >
                <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                  {/* anon_id prefix as a stable readable handle */}
                  {s.anonId.slice(0, 6)}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[var(--ink)]/85">
                  {s.lastPath}
                </span>
                {s.country ? (
                  <span
                    className="hidden font-mono text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--ink)]/65 sm:inline"
                    title={`country · ${s.country}`}
                  >
                    {countryFlag(s.country)} {s.country}
                  </span>
                ) : null}
                {s.deviceType || s.browser ? (
                  <span
                    className="hidden font-mono text-[9.5px] uppercase tracking-[0.06em] text-[var(--ink)]/55 sm:inline"
                    title={[s.deviceType, s.browser, s.os].filter(Boolean).join(' · ')}
                  >
                    {s.deviceType ?? '—'}
                    {s.browser ? ` · ${s.browser}` : ''}
                  </span>
                ) : null}
                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/65">
                  {plural(s.eventsLastWindow, { one: '# event', other: '# events' })}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/45">
                  {relativeAgoFrom(s.lastSeenAt, now)}
                </span>
                <span
                  aria-hidden
                  className="font-mono text-[14px] text-[var(--ink)]/30 transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--plum)]"
                >
                  →
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

/** Render an ISO 3166 alpha-2 code as its emoji flag. Pure offset
 *  arithmetic — no lookup table needed; every country code maps to the
 *  regional-indicator characters at U+1F1E6 + (letter - 'A'). Returns
 *  the input unchanged if it's not a valid 2-letter code so we never
 *  show an empty box. */
function countryFlag(iso: string): string {
  const code = iso.trim().toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return iso;
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  const a = String.fromCodePoint(base + code.charCodeAt(0));
  const b = String.fromCodePoint(base + code.charCodeAt(1));
  return `${a}${b}`;
}

function relativeAgoFrom(iso: string | null | undefined, nowMs: number): string {
  // Same NaN-safety as relativeAgo above. Used by the ActiveNowCard's
  // ticking labels so a malformed last-seen never renders "NaN ago".
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  // [i18n] Formatting delegated; the NaN/empty guards above are the
  // part worth keeping. Sub-5s renders a localized "now".
  return formatRelativeTimeCompact(t, { now: nowMs, nowThresholdMs: 5_000 });
}

// ─── Session drilldown ─────────────────────────────────────────────────
// Right-edge slide-over that shows one visitor's recent events ticking
// in. Polls every 3s while open so the founder can literally watch a
// click → scroll → conversion sequence land in near-real-time. Chrome
// (slide-in, plum edge, scrim + ESC close, scroll lock) comes from the
// shared <Drawer> shell.

function SessionDrilldown({
  brandId,
  session,
  onClose,
}: {
  brandId: string;
  session: AnalyticsActiveSession;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const { data } = useQuery({
    ...visitorEventsQuery(brandId, session.anonId),
    refetchInterval: 3_000,
    refetchIntervalInBackground: false,
  });
  const events = data?.events ?? [];
  // Tick once a second locally for the relative-ago labels.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const { visible, requestClose } = useDrawer(onClose);

  return (
    <Drawer
      visible={visible}
      onRequestClose={requestClose}
      maxWidthClass="max-w-[480px]"
      ariaLabel={t`session ${session.anonId.slice(0, 6)} live tail`}
    >
      <header className="flex items-start justify-between gap-3 border-b border-[var(--line)] bg-white px-5 py-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>live tail · session</Trans>
          </p>
          <p className="mt-1.5 truncate font-mono text-[13px] font-bold tracking-[-0.005em] text-[var(--ink)]">
            {session.anonId.slice(0, 12)}
          </p>
          <p className="mt-0.5 truncate font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>on · {session.lastPath}</Trans>
          </p>
        </div>
        <DrawerCloseButton onClick={requestClose} />
      </header>
      <div className="border-b border-[var(--line)] bg-[var(--paper)] px-5 py-2">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/65">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--pulse)] mag-pulse" />
          <Trans>polling every 3s · {events.length} events</Trans>
        </span>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {events.length === 0 ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>waiting for events on this session…</Trans>
          </p>
        ) : (
          <ul className="flex flex-col">
            {events.map((e) => (
              <li
                key={e.id}
                className="flex flex-col gap-1 border-b border-[var(--line)] py-2.5 last:border-b-0"
              >
                <div className="flex items-baseline gap-2">
                  <span className="inline-flex shrink-0 items-center rounded-full border border-[var(--line)] bg-white px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]/75">
                    {eventTypeLabel(e.type)}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[var(--ink)]/90">
                    {e.path}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/50">
                    {relativeAgoFrom(e.receivedAt, now)}
                  </span>
                </div>
                {e.referrer ? (
                  <p className="truncate font-mono text-[10.5px] text-[var(--ink)]/55">
                    {'↩'} <Trans>from {e.referrer}</Trans>
                  </p>
                ) : null}
                {e.deviceType || e.browser || e.os || e.country ? (
                  <p className="flex flex-wrap gap-x-2 font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/55">
                    {e.country ? (
                      <span>
                        {countryFlag(e.country)} {e.country}
                      </span>
                    ) : null}
                    {e.deviceType ? <span>↘ {e.deviceType}</span> : null}
                    {e.browser ? <span>· {e.browser}</span> : null}
                    {e.os ? <span>· {e.os}</span> : null}
                  </p>
                ) : null}
                {e.data ? <EventDataPreview data={e.data} eventType={e.type} /> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  );
}

function EventDataPreview({
  data,
  eventType,
}: {
  data: Record<string, unknown>;
  eventType: AnalyticsEventRow['type'];
}) {
  // identify events carry user-identifying traits (email, name, plan,
  // etc.) the founder posted via window.mag.identify. Keys are useful
  // signal ("we know plan + segment"); values are PII — render the
  // shape, redact the contents. All other event types pass through.
  const isIdentify = eventType === 'identify';
  // Tiny key:value list. Strings + numbers render directly; everything
  // else gets JSON.stringified short. Caps at the first 4 keys.
  const entries = Object.entries(data).slice(0, 4);
  if (entries.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10.5px] text-[var(--ink)]/65">
      {entries.map(([k, v]) => (
        <li key={k} className="flex items-baseline gap-1">
          <span className="uppercase tracking-[0.06em] text-[var(--ink)]/45">{k}</span>
          <span className="text-[var(--ink)]/80">
            {isIdentify && k !== 'user_id'
              ? '···'
              : typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
                ? String(v)
                : JSON.stringify(v).slice(0, 60)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function VisitsByDayCard({ visitsByDay }: { visitsByDay: AnalyticsVisitsByDay[] }) {
  const { t } = useLingui();
  const days = visitsByDay.length;
  const total = visitsByDay.reduce((acc, d) => acc + d.pageViews, 0);
  // Day-over-day delta: yesterday vs the day before. Skipped when we
  // don't have at least two days yet — early state where the number
  // would mislead more than help.
  const last = visitsByDay[days - 1];
  const prev = visitsByDay[days - 2];
  const delta = last && prev ? last.pageViews - prev.pageViews : null;
  const max = Math.max(1, ...visitsByDay.map((d) => d.pageViews));

  // SVG bar chart — no chart lib. Width responds via viewBox; the
  // bars are evenly spaced with a 2px gap and rounded tops. ink fill,
  // spell highlight on the most-recent day so today's bar reads as
  // "right now."
  const chartWidth = 600;
  const chartHeight = 80;
  const gap = 2;
  const barWidth = days > 0 ? (chartWidth - gap * (days - 1)) / days : chartWidth;
  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>visits · last {days}d</Trans>
        </p>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-[20px] font-black leading-none tracking-[-0.02em] text-[var(--ink)] tabular-nums">
            <CountUp value={total} />
          </span>
          {delta !== null ? (
            <span
              className={`font-mono text-[10px] uppercase tracking-[0.08em] ${
                delta > 0
                  ? 'text-[var(--ink)]'
                  : delta < 0
                    ? 'text-[var(--pulse)]'
                    : 'text-[var(--ink)]/55'
              }`}
            >
              {delta > 0 ? '↑' : delta < 0 ? '↓' : '·'}{' '}
              <Trans>{formatNumber(Math.abs(delta))} d/d</Trans>
            </span>
          ) : null}
        </div>
      </div>
      <div className="mt-4">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="none"
          className="h-[80px] w-full"
          role="img"
          aria-label={t`${days}-day visit trend`}
        >
          <title>{t`${formatNumber(total)} pageviews across the last ${days} days`}</title>
          {visitsByDay.map((d, i) => {
            const h = (d.pageViews / max) * chartHeight;
            const x = i * (barWidth + gap);
            const _y = chartHeight - h;
            const isToday = i === days - 1;
            const fill = isToday ? 'var(--spell)' : 'var(--ink)';
            // Always draw at least a 2px stub for zero days so the
            // chart's baseline reads as data, not absence.
            const drawHeight = Math.max(h, 2);
            const drawY = chartHeight - drawHeight;
            return (
              <rect
                key={d.date}
                x={x}
                y={drawY}
                width={barWidth}
                height={drawHeight}
                rx={Math.min(2, barWidth / 4)}
                fill={fill}
                opacity={d.pageViews === 0 ? 0.15 : 1}
              >
                <title>
                  {t`${d.date} · ${d.pageViews} pageviews · ${d.uniqueVisitors} unique`}
                </title>
              </rect>
            );
          })}
        </svg>
        <div className="mt-2 flex justify-between font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <span>{visitsByDay[0]?.date ?? ''}</span>
          <span>{visitsByDay[days - 1]?.date ?? ''}</span>
        </div>
      </div>
    </article>
  );
}

function AudienceCard({ audience }: { audience: AnalyticsAudience | null }) {
  const { t } = useLingui();
  if (!audience) return null;
  const empty =
    audience.devices.length === 0 &&
    audience.browsers.length === 0 &&
    audience.referrers.length === 0 &&
    audience.countries.length === 0;
  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>audience makeup · last {audience.windowDays}d</Trans>
      </p>
      {empty ? (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>not enough signal yet — give it a day or two of visitors.</Trans>
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          <FacetList title={t`countries`} facets={audience.countries} renderLabel={countryLabel} />
          <FacetList title={t`devices`} facets={audience.devices} />
          <FacetList title={t`browsers`} facets={audience.browsers} />
          <FacetList title={t`referrers`} facets={audience.referrers} />
        </div>
      )}
    </article>
  );
}

function countryLabel(iso: string): string {
  return `${countryFlag(iso)} ${iso}`;
}

function FacetList({
  title,
  facets,
  renderLabel,
}: {
  title: string;
  facets: AnalyticsTopFacet[];
  renderLabel?: (value: string) => string;
}) {
  return (
    <div>
      <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
        {'↘'} {title}
      </p>
      {facets.length === 0 ? (
        <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/40">
          <Trans>no data</Trans>
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5">
          {facets.map((f) => (
            <li key={f.value} className="relative overflow-hidden rounded-md">
              {/* Inline bar — share-based width, sits behind the
                  label so the row reads as a stacked rank at a
                  glance without becoming a chart. */}
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 bg-[var(--spell)]/35"
                style={{ width: `${Math.max(2, f.share * 100).toFixed(1)}%` }}
              />
              <div className="relative flex items-baseline justify-between gap-2 px-2 py-1.5">
                <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-[var(--ink)]/90">
                  {renderLabel ? renderLabel(f.value) : f.value}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/55">
                  {formatNumber(f.count)} · {Math.round(f.share * 100)}%
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentPathsCard({ pathClicks }: { pathClicks: AnalyticsPathClickCount[] }) {
  const top = pathClicks.slice(0, 8);
  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>where the clicks are landing</Trans>
      </p>
      {top.length === 0 ? (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>
            no click events in the window yet — pageviews fire automatically, clicks need real
            interaction.
          </Trans>
        </p>
      ) : (
        <ul className="mt-3 flex flex-col">
          {top.map((p) => (
            <li
              key={p.path}
              className="flex items-baseline justify-between gap-3 border-b border-[var(--line)] py-2 last:border-b-0"
            >
              <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[var(--ink)]/85">
                {p.path}
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--ink)]/65">
                <Trans>
                  {formatNumber(p.clicks)} clicks · {formatNumber(p.uniqueVisitors)} ppl
                </Trans>
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// Our own SDK event-type enum, rendered as the chip's prose — so it
// resolves through a translated label with the key intact. Built during
// render: a module-level t`` would bake English in at import time.
function eventTypeLabel(type: AnalyticsEventRow['type']): string {
  const labels: Record<AnalyticsEventRow['type'], string> = {
    page_view: i18n._(msg`page view`),
    click: i18n._(msg`click`),
    scroll: i18n._(msg`scroll`),
    conversion: i18n._(msg`conversion`),
    identify: i18n._(msg`identify`),
  };
  return labels[type] ?? type;
}

/** The conversion's own name, when the row is one.
 *
 *  Without this the feed renders a bare "CONVERSION" chip, and
 *  `signup_started` — which fires on a marketing-page CTA click — is
 *  indistinguishable from `account_created`. On 2026-10-05 that read as
 *  "someone signed up and paid" when nobody had done either. The name is
 *  already on the payload; the feed just never showed it. */
function conversionName(e: AnalyticsEventRow): string | null {
  if (e.type !== 'conversion') return null;
  const name = e.data?.['name'];
  return typeof name === 'string' && name.length > 0 ? name : null;
}

function RecentEventsCard({ events }: { events: AnalyticsEventRow[] }) {
  const recent = events.slice(0, 12);
  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>recent events · raw sdk feed</Trans>
      </p>
      <p className="mt-2 text-[12.5px] leading-[1.5] text-[var(--ink)]/65">
        <Trans>
          last few signals the sdk posted from your site. useful for "is the install working?" —
          mag's strategic reads still live in your home feed.
        </Trans>
      </p>
      {recent.length === 0 ? (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>no events yet in the rollup window.</Trans>
        </p>
      ) : (
        <ul className="mt-3 flex flex-col">
          {recent.map((e) => (
            <li
              key={e.id}
              className="flex items-baseline justify-between gap-3 border-b border-[var(--line)] py-2 last:border-b-0"
            >
              <span className="inline-flex shrink-0 items-center rounded-full border border-[var(--line)] bg-[var(--paper)] px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]/75">
                {eventTypeLabel(e.type)}
              </span>
              {conversionName(e) ? (
                <span className="shrink-0 font-mono text-[10px] font-semibold tracking-[0.04em] text-[var(--plum)]">
                  {conversionName(e)}
                </span>
              ) : null}
              <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[var(--ink)]/85">
                {e.path}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/55">
                {relativeAgo(e.clientTs)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function relativeAgo(iso: string | null | undefined): string {
  // Guard against malformed / missing timestamps. Without this, an
  // unparseable iso (or undefined coming through a stale api shape)
  // produced "NaN ago" in the recent-events feed. Falls back to "—"
  // and logs once per render so the underlying bad value is visible
  // in devtools without spamming the console (s162.37).
  if (!iso) {
    console.warn('[analytics] relativeAgo got empty timestamp');
    return '—';
  }
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) {
    console.warn('[analytics] relativeAgo got unparseable timestamp', iso);
    return '—';
  }
  // [i18n] Formatting delegated; the guards + warns above are the part
  // worth keeping. This one counts seconds, so the "now" window is 1s.
  return formatRelativeTimeCompact(t, { nowThresholdMs: 1_000 });
}

// ─── Tracker status (name + live-pill) — rename inline ─────────────────

function TrackerStatusCard({
  tracker,
  onChanged,
}: {
  tracker: AnalyticsProject;
  onChanged: () => Promise<void>;
}) {
  const { t } = useLingui();
  const isLive = Boolean(tracker.firstEventAt);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tracker.name);
  const [submitting, setSubmitting] = useState(false);

  // Re-sync local state when the upstream tracker name changes (e.g.
  // someone edited it in another tab and the route refetched).
  useEffect(() => {
    setName(tracker.name);
  }, [tracker.name]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      magToast.error(t`tracker name required.`, t`give it something you'll recognise.`);
      return;
    }
    if (trimmed === tracker.name) {
      setEditing(false);
      return;
    }
    setSubmitting(true);
    try {
      await updateAnalyticsProject(tracker.id, { name: trimmed });
      magToast.success(t`renamed.`);
      setEditing(false);
      await onChanged();
    } catch (err) {
      magToast.error("couldn't rename.", founderErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>tracker</Trans>
          </p>
          {editing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              maxLength={120}
              className="mt-1.5 w-full max-w-[320px] rounded-md border border-[var(--ink)] bg-white px-2 py-1 text-[15px] font-bold outline-none focus:shadow-[3px_3px_0_var(--plum)]"
            />
          ) : (
            <p className="mt-1.5 truncate text-[15px] font-bold tracking-[-0.01em] text-[var(--ink)]">
              {tracker.name}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] ${
              isLive
                ? 'bg-[var(--spell)] text-[var(--ink)]'
                : 'border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]/65'
            }`}
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                isLive ? 'mag-pulse bg-[var(--pulse)]' : 'bg-[var(--ink)]/25'
              }`}
            />
            {isLive ? t`live` : t`waiting on first event`}
          </span>
          {editing ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setName(tracker.name);
                  setEditing(false);
                }}
                disabled={submitting}
                className="link-quiet text-[12px] disabled:opacity-60"
              >
                <Trans>cancel</Trans>
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={submitting}
                className="btn btn-ghost text-[11px] disabled:opacity-60"
              >
                {submitting ? t`saving…` : t`save`}
                <span className="btn-arrow">→</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="link-quiet text-[12px]"
            >
              {'↘'} <Trans>rename</Trans>
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// ─── Allowed hosts — its own card so the constraint is impossible to ───
// miss. Events from anywhere not on this list are silently rejected by
// the api, which is easy to overlook in production.
function AllowedHostsCard({
  tracker,
  onChanged,
}: {
  tracker: AnalyticsProject;
  onChanged: () => Promise<void>;
}) {
  const { t } = useLingui();
  const [editing, setEditing] = useState(false);
  const [hosts, setHosts] = useState(tracker.allowedHosts.join(', '));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setHosts(tracker.allowedHosts.join(', '));
  }, [tracker.allowedHosts]);

  const handleSave = async () => {
    const parsed = hosts
      .split(/[,\n]/)
      .map((h) => h.trim())
      .filter((h) => h.length > 0);
    if (parsed.length === 0) {
      magToast.error(t`at least one host required.`, t`otherwise every event gets rejected.`);
      return;
    }
    setSubmitting(true);
    try {
      await updateAnalyticsProject(tracker.id, { allowedHosts: parsed });
      magToast.success(t`allowed hosts updated.`, t`events from anywhere else stay rejected.`);
      setEditing(false);
      await onChanged();
    } catch (err) {
      magToast.error("couldn't save.", founderErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  const empty = tracker.allowedHosts.length === 0;

  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>allowed hosts</Trans>
        </p>
        {editing ? null : (
          <button type="button" onClick={() => setEditing(true)} className="link-quiet text-[12px]">
            {'↘'} <Trans>edit</Trans>
          </button>
        )}
      </div>
      <p className="mt-2 max-w-[680px] text-[13px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          only events posted from these origins are accepted. anything else — a typo, the wrong
          domain, a forgotten subdomain — gets silently dropped and never shows up in mag's reads.
          keep the list tight, but make sure every host your site actually ships on is in it.
        </Trans>
      </p>

      {editing ? (
        <div className="mt-4 flex flex-col gap-3">
          <textarea
            value={hosts}
            onChange={(e) => setHosts(e.target.value)}
            placeholder={t`magig.app, www.magig.app`}
            disabled={submitting}
            rows={3}
            maxLength={1000}
            className="w-full rounded-lg border border-[var(--ink)] bg-white px-3 py-2 font-mono text-[13px] outline-none focus:shadow-[3px_3px_0_var(--plum)]"
          />
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>comma- or newline-separated. hostnames only — no protocol, no path.</Trans>
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setHosts(tracker.allowedHosts.join(', '));
                setEditing(false);
              }}
              disabled={submitting}
              className="link-quiet text-[12px] disabled:opacity-60"
            >
              <Trans>cancel</Trans>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting}
              className="btn disabled:opacity-60"
            >
              {submitting ? t`saving…` : t`save hosts`}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      ) : empty ? (
        <p className="mt-4 inline-flex items-center gap-2 rounded-md border border-[var(--pulse)]/40 bg-[var(--pulse)]/5 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--pulse)]">
          {'↘'} <Trans>no hosts set — every event will be rejected</Trans>
        </p>
      ) : (
        <ul className="mt-4 flex flex-wrap gap-2">
          {tracker.allowedHosts.map((h) => (
            <li
              key={h}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--paper)] px-3 py-1 font-mono text-[12px] tracking-[-0.005em] text-[var(--ink)]/85"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--spell)]" />
              {h}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// ─── Server-side write key ────────────────────────────────────────────
//
// The secret a founder's backend uses for webhook ingest. Keys generated
// today are recoverable (ciphertext stored alongside the hash), so the
// panel leads with SEEING the key you have. Older keys predate that and
// can only be rotated — `writeKeyRecoverable` separates the two.
//
// The inversion matters. This panel used to offer exactly one action and
// it was the destructive one: a primary "rotate write key" button, on a
// card whose own copy warned that rotating kills every live webhook. The
// safe, common intent — "show me my key" — wasn't expressible at all.
// Now reveal/copy is primary and rotate is a quiet link, keeping its
// two-step confirm.
function WriteKeyCard({
  tracker,
  onChanged,
}: {
  tracker: AnalyticsProject;
  onChanged: () => Promise<void>;
}) {
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [reveal, setReveal] = useState<{ writeKey: string; createdAt: string } | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);
  // Two-step confirm flow for rotation: first click arms "yes, rotate",
  // second click commits. Stops a stray click from invalidating the
  // founder's live payment webhook by mistake.
  const [armedToRotate, setArmedToRotate] = useState(false);
  useEffect(() => {
    if (!armedToRotate) return;
    const t = window.setTimeout(() => setArmedToRotate(false), 4000);
    return () => window.clearTimeout(t);
  }, [armedToRotate]);

  // Has this key ever actually received anything? Its own query rather
  // than a field on the tracker read: it's a ClickHouse hit, and paying
  // for it on every analytics page load to serve one line of one card is
  // the wrong trade. Refetches after a rotate via the shared key below.
  const { data: status } = useQuery({
    queryKey: ['analytics-server-status', tracker.id],
    queryFn: () => getAnalyticsServerStatus(tracker.id),
    staleTime: 60_000,
  });

  const isRotation = tracker.hasWriteKey;
  const canReveal = tracker.hasWriteKey && tracker.writeKeyRecoverable;
  // A key that exists but can't be shown — generated before we kept a
  // readable copy. Today this is the MAJORITY state, not an edge case:
  // every tracker that had a key before this shipped is in it.
  const isLegacy = tracker.hasWriteKey && !tracker.writeKeyRecoverable;

  const handleGenerate = async () => {
    if (isRotation && !armedToRotate) {
      setArmedToRotate(true);
      return;
    }
    setSubmitting(true);
    try {
      const result = await setAnalyticsProjectWriteKey(tracker.id);
      setReveal(result);
      setShown(null);
      setArmedToRotate(false);
      await onChanged();
    } catch (err) {
      magToast.error("couldn't generate.", founderErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReveal = async () => {
    if (shown) {
      setShown(null);
      return;
    }
    setRevealing(true);
    try {
      const key = await revealAnalyticsProjectWriteKey(tracker.id);
      if (key) setShown(key);
      else
        magToast.error(
          t`can't show this one.`,
          t`it predates recoverable storage — rotate to get a readable key.`,
        );
    } catch (err) {
      magToast.error("couldn't read the key.", founderErrorCopy(err));
    } finally {
      setRevealing(false);
    }
  };

  const handleCopy = async () => {
    const key = shown ?? (await revealAnalyticsProjectWriteKey(tracker.id).catch(() => null));
    if (!key) {
      magToast.error(t`nothing to copy.`, t`reveal the key first.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      magToast.error(t`clipboard refused.`, t`reveal the key and copy it by hand.`);
    }
  };

  return (
    <>
      <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>server write key</Trans>
          </p>
          {tracker.hasWriteKey ? (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              <Trans>set</Trans>{' '}
              {tracker.writeKeyCreatedAt
                ? formatDate(tracker.writeKeyCreatedAt, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                : ''}
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              <Trans>not generated</Trans>
            </span>
          )}
        </div>
        <p className="mt-2 max-w-[680px] text-[13px] leading-[1.5] text-[var(--ink)]/70">
          <Trans>lets your backend post events from places where no browser exists.</Trans>
        </p>

        {/* Label/value rows rather than prose. The card used to EXPLAIN
            what a write key is and never say whether yours was working;
            the founder's actual question is "is my webhook landing?",
            and after that, "what do I post, and where?" — facts that
            otherwise live only in a modal they saw once, at generation. */}
        <dl className="mt-4 space-y-2.5 rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <dt className="w-[70px] shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              <Trans>key</Trans>
            </dt>
            <dd className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {canReveal ? (
                <>
                  <code className="min-w-0 flex-1 break-all font-mono text-[13px] text-[var(--ink)]">
                    {shown ?? 'mag_sk_••••••••••••••••'}
                  </code>
                  <button
                    type="button"
                    onClick={handleReveal}
                    disabled={revealing}
                    className="btn btn-ghost shrink-0 text-[12px] disabled:opacity-60"
                  >
                    {revealing ? t`reading…` : shown ? t`hide` : t`reveal`}
                  </button>
                  <button type="button" onClick={handleCopy} className="btn shrink-0 text-[12px]">
                    {copied ? t`copied ✓` : t`copy`}
                  </button>
                </>
              ) : (
                <span className="text-[13px] leading-[1.45] text-[var(--ink)]/55">
                  <Trans>
                    can't be shown — made before magig kept a readable copy, so only its hash is
                    stored. it still works.
                  </Trans>
                </span>
              )}
            </dd>
          </div>

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <dt className="w-[70px] shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              <Trans>status</Trans>
            </dt>
            <dd className="min-w-0 flex-1 text-[13px] leading-[1.45]">
              {status === undefined ? (
                <span className="text-[var(--ink)]/40">
                  <Trans>checking…</Trans>
                </span>
              ) : status.count === 0 ? (
                // The whole point of the row. A webhook that was never
                // wired up looks identical to one that was, until here.
                <span className="text-[var(--ink)]/60">
                  {'○'} <Trans>never used — no server events received</Trans>
                </span>
              ) : (
                <span className="text-[var(--ink)]">
                  {'●'}{' '}
                  <Trans>
                    {status.count} events · last {formatRelativeTimeCompact(status.lastAt)}
                  </Trans>
                  {status.recentNames.length > 0 ? (
                    <span className="text-[var(--ink)]/55"> · {status.recentNames.join(', ')}</span>
                  ) : null}
                </span>
              )}
            </dd>
          </div>

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <dt className="w-[70px] shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              <Trans>send to</Trans>
            </dt>
            <dd className="min-w-0 flex-1 break-all font-mono text-[12.5px] text-[var(--ink)]/80">
              POST {SDK_INGEST_URL}/track
            </dd>
          </div>

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <dt className="w-[70px] shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              <Trans>header</Trans>
            </dt>
            <dd className="min-w-0 flex-1 break-all font-mono text-[12.5px] text-[var(--ink)]/80">
              X-Mag-Write-Key: $MAG_WRITE_KEY
            </dd>
          </div>
        </dl>

        {/* Which action is PRIMARY follows the state, not how destructive
            the operation is — it's the same rotate underneath all three:
              no key      → generate. the only thing to do.
              legacy key  → generating a readable one is the only useful
                            action left, so it gets a real button. Burying
                            the sole available action behind a quiet link
                            reads as "here is a problem, and here is a
                            link we'd rather you didn't click".
              readable key → you already have what you need, so rotate
                            demotes to a quiet link.
            The two-step confirm and the consequence line ride along in
            every case, so promoting the button never costs the guard. */}
        <div className="mt-4 flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
          {armedToRotate ? (
            <span className="mr-auto text-[12px] leading-[1.4] text-[var(--err)]">
              <Trans>every webhook on the old key stops ingesting immediately.</Trans>
            </span>
          ) : null}
          {armedToRotate ? (
            <button
              type="button"
              onClick={() => setArmedToRotate(false)}
              disabled={submitting}
              className="link-quiet text-[12px] disabled:opacity-60"
            >
              <Trans>cancel</Trans>
            </button>
          ) : null}
          {canReveal ? (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={submitting}
              className="text-[12px] text-[var(--ink)]/50 underline decoration-dotted underline-offset-4 transition-colors hover:text-[var(--err)] disabled:opacity-60"
            >
              {submitting ? t`rotating…` : armedToRotate ? t`confirm rotate` : t`rotate key`}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={submitting}
              className="btn disabled:opacity-60"
            >
              {submitting
                ? t`generating…`
                : armedToRotate
                  ? t`confirm — replace the key`
                  : isLegacy
                    ? t`generate a readable key`
                    : t`generate write key`}
              <span className="btn-arrow">→</span>
            </button>
          )}
        </div>
      </article>

      {reveal ? <WriteKeyRevealModal reveal={reveal} onClose={() => setReveal(null)} /> : null}
    </>
  );
}

/** Modal shown immediately after generate/rotate. Plaintext write key
 *  with a copy button, an explicit "i've saved it" confirmation to close
 *  (so the founder can't dismiss it by accident). The plaintext lives
 *  only in this component's local state and dies on unmount. */
function WriteKeyRevealModal({
  reveal,
  onClose,
}: {
  reveal: { writeKey: string; createdAt: string };
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(reveal.writeKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      magToast.error(t`clipboard refused.`, t`select the key and copy by hand.`);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--ink)]/55 px-4 backdrop-blur-sm">
      <div className="w-full max-w-[560px] rounded-2xl border-[1.5px] border-[var(--ink)] bg-[var(--cream)] p-6 shadow-[6px_6px_0_var(--ink)]">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>server write key · save this now</Trans>
        </p>
        <p className="mt-2 font-serif text-[20px] leading-tight text-[var(--ink)]">
          <Trans>“stash it in your password manager — i don't keep a copy.”</Trans>
        </p>
        <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/75">
          {/* The three <code> spans are an env var name, a header name
              and a route — identifiers, held in expression containers so
              they read as data and stay out of the message. */}
          <Trans>
            this is the only time the plaintext appears anywhere. paste it into your webhook config
            (mercadopago / stripe / your server env) under the env var name{' '}
            <code className="rounded bg-[var(--paper)] px-1.5 py-0.5 font-mono text-[12px]">
              {'MAG_WRITE_KEY'}
            </code>
            . use it as the value of the{' '}
            <code className="rounded bg-[var(--paper)] px-1.5 py-0.5 font-mono text-[12px]">
              {'X-Mag-Write-Key'}
            </code>{' '}
            header on{' '}
            <code className="rounded bg-[var(--paper)] px-1.5 py-0.5 font-mono text-[12px]">
              {'POST /ingest/v1/track'}
            </code>
            .
          </Trans>
        </p>
        <div className="mt-4 rounded-lg border border-[var(--ink)] bg-white p-3">
          <code className="block break-all font-mono text-[13px] leading-[1.5] text-[var(--ink)]">
            {reveal.writeKey}
          </code>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onCopy} className="btn btn-ghost text-[12px]">
            {copied ? t`copied ✓` : t`copy key`}
            {copied ? null : <span className="btn-arrow">→</span>}
          </button>
          <button type="button" onClick={onClose} className="btn">
            <Trans>i've saved it</Trans>
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── No-tracker fallback (rare: pre-s86 brand, or scrape skipped) ──────

function SetUpTrackingPrompt({
  brandId,
  onCreated,
}: {
  brandId: string;
  onCreated: () => Promise<void>;
}) {
  const { t } = useLingui();
  return (
    <div className="mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
      <div className="flex items-start gap-3">
        <Mag size={36} mood="smug" />
        <div className="flex-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>no tracker yet</Trans>
          </p>
          <p className="mt-1.5 font-serif text-[18px] leading-tight text-[var(--ink)]">
            <Trans>
              “we usually wire this up during onboarding. name your site and the hostname events
              should come from — i'll generate the snippet.”
            </Trans>
          </p>
          <CreateTrackerFields
            brandId={brandId}
            onCreated={onCreated}
            ctaLabel={t`set up tracking`}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Advanced disclosure: switch / add trackers ────────────────────────

function AdvancedDisclosure({
  trackers,
  selectedId,
  onSelect,
  brandId,
  onChanged,
  tuckedInstall,
}: {
  trackers: AnalyticsProject[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  brandId: string;
  onChanged: () => Promise<void>;
  /** Once events are flowing, the install snippet + agent prompt move
   *  inside this disclosure (rarely needed but reachable for reinstalls
   *  or new subdomains). Null in pre-event mode where they live above. */
  tuckedInstall: AnalyticsProject | null;
}) {
  const { t } = useLingui();
  const [adding, setAdding] = useState(false);
  const hasExtras = trackers.length > 1;
  return (
    <details className="mt-8">
      <summary className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]/80">
        {'↘'} <Trans>advanced</Trans>
        {hasExtras
          ? ` · ${plural(trackers.length, {
              one: '# tracker on this brand',
              other: '# trackers on this brand',
            })}`
          : ''}
      </summary>
      <div className="mt-3 flex flex-col gap-4">
        <p className="max-w-[680px] text-[13px] leading-[1.5] text-[var(--ink)]/65">
          <Trans>
            most brands run one tracker. add another only when you want findings scoped separately
            for a different site or subdomain. switching here changes which tracker the panels above
            show.
          </Trans>
        </p>
        {hasExtras ? (
          <div className="flex flex-wrap items-center gap-2">
            {trackers.map((t) => {
              const active = t.id === selectedId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onSelect(t.id)}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] ${
                    active
                      ? 'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] text-[var(--ink)]'
                      : 'border border-[var(--line)] bg-white text-[var(--ink)]/65 hover:text-[var(--ink)]'
                  }`}
                >
                  {t.name}
                  {t.firstEventAt ? (
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--pulse)]" />
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}
        {tuckedInstall ? (
          <div className="flex flex-col gap-4">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
              {'↘'} <Trans>reinstall · for a fresh site or a new subdomain</Trans>
            </p>
            <InstallPanel project={tuckedInstall} />
            <AgentPromptPanel project={tuckedInstall} />
          </div>
        ) : null}
        {adding ? (
          <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
            <div className="flex items-start gap-3">
              <Mag size={36} mood="casting" />
              <div className="flex-1">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
                  {'↘'} <Trans>new tracker</Trans>
                </p>
                <p className="mt-1.5 font-serif text-[18px] leading-tight text-[var(--ink)]">
                  <Trans>“separate site or subdomain? name it, point me at the hosts.”</Trans>
                </p>
                <CreateTrackerFields
                  brandId={brandId}
                  onCreated={async () => {
                    setAdding(false);
                    await onChanged();
                  }}
                  onCancel={() => setAdding(false)}
                  ctaLabel={t`add tracker`}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="link-quiet text-[12px]"
            >
              {'↘'} <Trans>add another tracker</Trans>
            </button>
          </div>
        )}
      </div>
    </details>
  );
}

// ─── Shared form: create / set-up a tracker for this brand ─────────────

function CreateTrackerFields({
  brandId,
  onCreated,
  onCancel,
  ctaLabel,
}: {
  brandId: string;
  onCreated: () => Promise<void>;
  onCancel?: () => void;
  ctaLabel: string;
}) {
  const { t } = useLingui();
  const [name, setName] = useState('');
  const [hosts, setHosts] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    if (!name.trim() || !hosts.trim()) {
      magToast.error(
        t`mag needs a name + at least one host.`,
        t`e.g. "magig.app" or "blog.magig.app".`,
      );
      return;
    }
    setSubmitting(true);
    try {
      const allowedHosts = hosts
        .split(/[,\n]/)
        .map((h) => h.trim())
        .filter((h) => h.length > 0);
      await createAnalyticsProject({ name: name.trim(), allowedHosts }, brandId);
      magToast.success(t`tracker ready.`, t`snippet + agent prompt below — paste and ship.`);
      await onCreated();
    } catch (err) {
      magToast.error("couldn't create the tracker.", founderErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="field">
          <span className="field-label">
            <Trans>tracker name</Trans>
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t`magig.app`}
            disabled={submitting}
            maxLength={120}
          />
          <span className="field-hint">
            <Trans>how you'll recognise it later.</Trans>
          </span>
        </label>
        <label className="field">
          <span className="field-label">
            <Trans>allowed hosts</Trans>
          </span>
          <textarea
            value={hosts}
            onChange={(e) => setHosts(e.target.value)}
            placeholder={t`magig.app, www.magig.app`}
            disabled={submitting}
            rows={3}
            maxLength={1000}
          />
          <span className="field-hint">
            <Trans>
              comma- or newline-separated hostnames. events from anywhere else get rejected.
            </Trans>
          </span>
        </label>
      </div>
      <div className="mt-4 flex items-center justify-end gap-3">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="link-quiet text-[12px]"
          >
            <Trans>cancel</Trans>
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleCreate}
          disabled={submitting}
          className="btn disabled:opacity-60"
        >
          {submitting ? t`creating…` : ctaLabel}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </>
  );
}

// ─── Conversion forwarding ─────────────────────────────────────────────

// Third-party product names. Not copy in any locale — "Meta CAPI" is
// "Meta CAPI" in Spanish — so this one stays a module-level const.
const PLATFORM_LABEL: Record<ConversionPlatform, string> = {
  meta: 'Meta CAPI',
  google: 'Google Enhanced Conversions',
  tiktok: 'TikTok Events API',
};

/** What one platform row says about itself.
 *
 *  META IS NOT MOCKED ANY MORE [task 272644f]. Its conversions go out through
 *  the batch upload scheduler once credentials exist, so a row reading
 *  "forwarding · mocked" would claim a dry run while money moved — the same
 *  lie the status='mocked' log row was retired for. An enabled meta row with
 *  a HALF-FILLED config is the worst case to render vaguely: the founder has
 *  done some of the work and nothing is sending, so the row names the missing
 *  half rather than saying "on".
 *
 *  google and tiktok keep the mock wording, because for them it is still
 *  true — their forwarders are the Mock impls in conversion-forwarders.ts and
 *  they really do only write a log row. */
//  TAKES NO `t`, AND THAT IS THE FIX [task 0bff688]. It used to accept one as
//  a PARAMETER, which silently defeated the lingui macro: `t` has to be the
//  macro identifier at COMPILE time for the template literal to be extracted
//  and swapped for a catalog lookup. Arriving as a function argument it stayed
//  a runtime call on a plain function, so every one of these six strings
//  rendered EMPTY in production from 2026-10-11 until now.
//
//  The tests did not catch it because they injected a stub that joined the
//  literal back together, which is exactly the English the macro would have
//  produced — green tests, blank UI. `i18n._(msg`...`)` is the form that works
//  outside a component: `msg` is extracted at build time and `i18n._` resolves
//  it at call time.
export function forwardStatusLabel(
  platform: ConversionPlatform,
  enabled: boolean,
  config: ConversionForwardConfig | null,
): string {
  if (!enabled) return i18n._(msg`off`);
  if (platform !== 'meta') return i18n._(msg`forwarding · mocked`);
  if (capiCredentialsComplete(config)) return i18n._(msg`forwarding · live`);
  if (hasCapiPixelId(config)) return i18n._(msg`needs a token`);
  if (hasCapiAccessToken(config)) return i18n._(msg`needs a pixel id`);
  return i18n._(msg`needs credentials`);
}

function ConversionForwardingPanel({ project }: { project: AnalyticsProject }) {
  const { t } = useLingui();
  const [envelope, setEnvelope] = useState<ConversionForwardsEnvelope | null>(null);
  const [pendingPlatform, setPendingPlatform] = useState<ConversionPlatform | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchConversionForwards(project.id)
      .then((e) => {
        if (cancelled) return;
        setEnvelope(e);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(founderErrorCopy(err, 'load failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  /** Fold one updated row back into the loaded envelope — shared by the
   *  toggle and by the credential save, which both get a full row back. */
  const applyForward = (platform: ConversionPlatform, updated: ConversionForwardConfig) => {
    setEnvelope((prev) => {
      if (!prev) return prev;
      const existing = prev.forwards.findIndex((f) => f.platform === platform);
      const nextForwards =
        existing >= 0
          ? prev.forwards.map((f) => (f.platform === platform ? updated : f))
          : [...prev.forwards, updated];
      return { ...prev, forwards: nextForwards };
    });
  };

  const handleToggle = async (platform: ConversionPlatform, next: boolean) => {
    setPendingPlatform(platform);
    try {
      const updated = await setConversionForward({
        projectId: project.id,
        platform,
        enabled: next,
      });
      applyForward(platform, updated);
      magToast.success(
        next
          ? t`mag will forward to ${PLATFORM_LABEL[platform]}.`
          : t`${PLATFORM_LABEL[platform]} off.`,
      );
    } catch (err) {
      magToast.error("couldn't update.", founderErrorCopy(err));
    } finally {
      setPendingPlatform(null);
    }
  };

  const findConfig = (platform: ConversionPlatform): ConversionForwardConfig | null => {
    return envelope?.forwards.find((f) => f.platform === platform) ?? null;
  };

  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>forward conversions to ad platforms</Trans>
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          <Trans>meta is live with creds · google and tiktok still mocked</Trans>
        </span>
      </div>
      <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          when a conversion fires on your site (via{' '}
          <code className="font-mono text-[12px] text-[var(--ink)]">{'window.mag.track(...)'}</code>
          ), mag forwards it to the ad platforms below so they optimise toward paying customers, not
          clicks. each toggle is per-project. meta sends for real once you add a pixel id and a
          conversions api token below — until then, and for google and tiktok, mag writes dry-run
          records to the audit log instead of hitting the live api.
        </Trans>
      </p>

      {error ? (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--pulse)]">
          ↘ {error}
        </p>
      ) : null}

      <ul className="mt-4 flex flex-col gap-2">
        {(['meta', 'google', 'tiktok'] as const).map((platform) => {
          const config = findConfig(platform);
          const enabled = config?.enabled ?? false;
          const pending = pendingPlatform === platform;
          return (
            <li
              key={platform}
              className="flex flex-col rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[13px] font-bold tracking-[-0.01em] text-[var(--ink)]">
                    {PLATFORM_LABEL[platform]}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                    {forwardStatusLabel(platform, enabled, config)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle(platform, !enabled)}
                  disabled={pending || !envelope}
                  className={`rounded-full px-3 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] disabled:opacity-50 ${
                    enabled
                      ? 'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] text-[var(--ink)]'
                      : 'border border-[var(--line)] bg-white text-[var(--ink)]/65 hover:text-[var(--ink)]'
                  }`}
                >
                  {pending ? t`saving…` : enabled ? t`on` : t`off`}
                </button>
              </div>
              {platform === 'meta' ? (
                <MetaCapiCredentialFields
                  projectId={project.id}
                  config={config}
                  onSaved={(updated) => applyForward('meta', updated)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>

      {envelope && envelope.recentLogs.length > 0 ? (
        <div className="mt-4">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>last 20 forwards</Trans>
          </p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {envelope.recentLogs.map((log) => (
              <ForwardLogRow key={log.id} log={log} />
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}

/** Why we could not list pixels, as something a founder can act on.
 *
 *  Each refusal maps to a DIFFERENT action, which is the entire reason the
 *  api does not collapse them: "connect meta", "re-connect meta asking for
 *  ads" and "pick an ad account" are three different afternoons. The
 *  catch-all deliberately does NOT guess — it says the list is unavailable
 *  and points at the manual fields, which always work. */
//  Same parameter-`t` defect as forwardStatusLabel above, and worse in effect:
//  these five sentences are the ONLY thing telling a founder how to fix a
//  broken pixel connection, and all five rendered empty [task 0bff688].
export function metaPixelRefusalCopy(refusal: MetaPixelRefusal): string {
  switch (refusal) {
    case 'no_meta_connection':
      return i18n._(msg`connect meta and mag can find your pixel for you — or paste the id below.`);
    case 'connection_missing_scope':
      return i18n._(
        msg`your meta connection cannot see ads. reconnect and allow ads access, or paste the id below.`,
      );
    case 'connection_token_expired':
      return i18n._(
        msg`your meta login expired. reconnect meta, or paste a token below — a token does not expire.`,
      );
    case 'no_ad_account':
      return i18n._(msg`pick an ad account on your meta connection and mag can list its pixels.`);
    default:
      return i18n._(
        msg`couldn't list your pixels right now. paste the id below and mag will use that.`,
      );
  }
}

/** Meta's CAPI credentials — the pixel id and the access token the batch
 *  uploader needs [task 272644f]. Until these are saved, every upload tick
 *  resolves 'no_config' and no meta conversion is ever sent.
 *
 *  The token is WRITE-ONLY by construction. The api answers reads with key
 *  NAMES and never values, so this component can report that a token is
 *  stored and literally cannot render it — there is nothing to leak into the
 *  DOM, a screenshot, or a browser extension. Replacing means typing a new
 *  one, which is the correct affordance for a secret anyway. */
export function MetaCapiCredentialFields({
  projectId,
  config,
  onSaved,
  /** Injected by the tests. Left undefined in the app, where the component
   *  fetches its own list on mount. */
  pixelsOverride,
}: {
  projectId: string;
  config: ConversionForwardConfig | null;
  onSaved: (updated: ConversionForwardConfig) => void;
  pixelsOverride?: MetaPixelsEnvelope;
}) {
  const { t } = useLingui();
  const tokenStored = hasCapiAccessToken(config);
  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [envelope, setEnvelope] = useState<MetaPixelsEnvelope | null>(pixelsOverride ?? null);

  useEffect(() => {
    if (pixelsOverride) return;
    let cancelled = false;
    fetchMetaPixels(projectId)
      .then((e) => {
        if (!cancelled) setEnvelope(e);
      })
      .catch(() => {
        // A failed LIST must not break the paste path — the manual fields are
        // the fallback, and they are the only thing that works at all before
        // meta's app review clears. Swallowed on purpose: there is nothing
        // the founder would do differently, and the inputs still work.
        if (!cancelled) setEnvelope({ pixels: null, refusal: 'unreachable', detail: null });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, pixelsOverride]);

  const options = envelope?.pixels ?? null;
  /** Mag has a usable login for this brand — the token field is then a
   *  nicety rather than a requirement. */
  const connectionCanSend = Boolean(envelope?.pixels);

  // The stored pixel id is not a secret, so the api does hand back its
  // presence — but not its value, since configKeys carries names only. An
  // empty box with "saved" beside it is the honest rendering.
  const pixelStored = hasCapiPixelId(config);
  const dirty = pixelId.trim().length > 0 || accessToken.length > 0;

  useEffect(() => {
    // Exactly one pixel is the common case, and making the founder open a
    // one-item dropdown is pointless. It is PRE-SELECTED, not saved: a bound
    // pixel nobody looked at is how the wrong dataset starts receiving
    // conversions, so the save still has to be pressed.
    if (!options || options.length !== 1 || pixelStored) return;
    const only = options[0];
    if (only) setPixelId((prev) => (prev === '' ? only.id : prev));
  }, [options, pixelStored]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await setConversionForward({
        projectId,
        platform: 'meta',
        // Only send what was actually typed. The api merges, so an untouched
        // field keeps whatever is already stored rather than clearing it.
        ...(pixelId.trim() ? { pixelId: pixelId.trim() } : {}),
        ...(accessToken ? { accessToken } : {}),
      });
      onSaved(updated);
      setPixelId('');
      setAccessToken('');
      magToast.success(t`mag can send conversions to meta now.`);
    } catch (err) {
      setError(founderErrorCopy(err, 'save failed'));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await setConversionForward({
        projectId,
        platform: 'meta',
        clearCredentials: true,
      });
      onSaved(updated);
      setPixelId('');
      setAccessToken('');
      magToast.success(t`meta credentials removed.`);
    } catch (err) {
      setError(founderErrorCopy(err, 'remove failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 border-t border-[var(--line)] pt-2">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {/* htmlFor rather than wrapping: the control is behind a ternary, so
            the implicit association biome looks for is not visible to it —
            and a screen reader would have the same problem. */}
        <label className="field" htmlFor="meta-capi-pixel">
          <span className="field-label">
            <Trans>pixel</Trans>
          </span>
          {options && options.length > 0 ? (
            <select
              id="meta-capi-pixel"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              disabled={saving}
            >
              {/* An explicit empty option even when one pixel auto-fills:
                  binding a dataset the founder never looked at is how the
                  wrong pixel starts receiving conversions. */}
              <option value="">{pixelStored ? t`keep the saved pixel` : t`choose a pixel…`}</option>
              {options.map((px) => (
                <option key={px.id} value={px.id}>
                  {px.name} · {px.id}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="meta-capi-pixel"
              type="text"
              inputMode="numeric"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              placeholder={pixelStored ? t`saved` : t`1234567890`}
              disabled={saving}
              maxLength={30}
            />
          )}
          <span className="field-hint">
            {options && options.length > 0 ? (
              <Trans>from your connected ad account.</Trans>
            ) : (
              <Trans>events manager → your pixel → the numeric id.</Trans>
            )}
          </span>
        </label>
        {/* htmlFor rather than wrapping: PasswordInput nests its input inside
            a positioning div for the show/hide button, so an implicit label
            association would not reach the control. */}
        <label className="field" htmlFor="meta-capi-access-token">
          <span className="field-label">
            <Trans>conversions api token</Trans>
          </span>
          <PasswordInput
            id="meta-capi-access-token"
            value={accessToken}
            onChange={setAccessToken}
            autoComplete="off"
            minLength={1}
            placeholder={tokenStored ? t`stored · type a new one to replace` : t`EAAG…`}
          />
          <span className="field-hint">
            {connectionCanSend ? (
              <Trans>
                optional — mag can use your connected meta login. a token here never expires, which
                a login does not.
              </Trans>
            ) : (
              <Trans>events manager → settings → generate access token.</Trans>
            )}
          </span>
        </label>
      </div>

      {error ? (
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--pulse)]">
          ↘ {error}
        </p>
      ) : null}

      {envelope?.refusal ? (
        <p className="mt-2 text-[12px] leading-[1.5] text-[var(--ink)]/60">
          ↘ {metaPixelRefusalCopy(envelope.refusal)}
        </p>
      ) : null}

      <div className="mt-2 flex items-center justify-end gap-2">
        {pixelStored || tokenStored ? (
          <button
            type="button"
            onClick={clear}
            disabled={saving}
            className="rounded-full border border-[var(--line)] bg-white px-3 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/65 hover:text-[var(--ink)] disabled:opacity-50"
          >
            <Trans>remove</Trans>
          </button>
        ) : null}
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="rounded-full border-[1.5px] border-[var(--ink)] bg-[var(--spell)] px-3 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)] disabled:opacity-50"
        >
          {saving ? t`saving…` : <Trans>save</Trans>}
        </button>
      </div>
    </div>
  );
}

function ForwardLogRow({ log }: { log: ConversionForwardLogEntry }) {
  const { t } = useLingui();
  const statusClass =
    log.status === 'sent'
      ? 'bg-[var(--spell)] text-[var(--ink)]'
      : log.status === 'mocked'
        ? 'bg-[var(--paper)] text-[var(--ink)]/65 border border-[var(--line)]'
        : 'bg-[var(--pulse)] text-[var(--cream)]';
  return (
    <li className="flex items-center gap-3 rounded-md border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 font-mono text-[11px]">
      <span
        className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.08em] ${statusClass}`}
      >
        {log.status === 'sent' ? t`sent` : log.status === 'mocked' ? t`mocked` : log.status}
      </span>
      <span className="flex-1 truncate text-[var(--ink)]/85">
        {PLATFORM_LABEL[log.platform]} · {log.conversionName}
      </span>
      <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {formatDateTime(log.attemptedAt)}
      </span>
    </li>
  );
}

// ─── Install snippet (founder copies once during setup) ─────────────────

// The snippet the founder pastes onto their deployed site must always
// point at the production ingest endpoint, regardless of where THIS
// dashboard is running. Earlier code interpolated env.API_URL into
// data-ingest-url, which baked "http://localhost:3001" into snippets
// shown in local dev — useless on a real site. SDK_INGEST_URL and
// SDK_SCRIPT_URL are the production-only constants; if local SDK
// testing is ever needed, edit the snippet by hand.
function InstallPanel({ project }: { project: AnalyticsProject }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  const snippet = buildInstallTag(project.key, project.allowedHosts[0] ?? null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      magToast.error(t`couldn't copy.`, t`select + copy manually.`);
    }
  };

  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>install · two scripts, paste in {'<head>'}</Trans>
      </p>
      <p className="mt-1 text-[12.5px] leading-[1.45] text-[var(--ink)]/65">
        <Trans>
          Paste both, in order — the inline stub first, then the loader. The stub captures any
          events your site fires before mag.js finishes downloading so nothing's lost on the first
          page load.
        </Trans>
      </p>
      <pre className="mt-3 overflow-x-auto rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3 font-mono text-[12px] leading-[1.5] text-[var(--ink)]/90">
        {snippet}
      </pre>
      <div className="mt-3 flex items-center justify-end">
        <button type="button" onClick={handleCopy} className="btn btn-ghost text-[11px]">
          {copied ? t`copied` : t`copy snippet`}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </article>
  );
}

// ─── Agent prompt — paste this into Cursor/Claude Code/etc. ────────────

function AgentPromptPanel({ project }: { project: AnalyticsProject }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  // Single source of truth — same prompt the onboarding checklist +
  // install nudge emit, so the install instructions never drift between
  // surfaces.
  const prompt = buildAgentPromptFromKey(project.key, project.allowedHosts[0] ?? null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      magToast.error(t`couldn't copy.`, t`select + copy manually.`);
    }
  };

  return (
    <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>agent prompt · paste into cursor / claude code / aider</Trans>
        </p>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          <Trans>one paste · agent handles the rest</Trans>
        </span>
      </div>
      <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          copy this and paste it into your dev agent inside your site's repo. it'll install the
          script tag in the right place for your framework and wire conversion tracking to the
          events you care about.
        </Trans>
      </p>
      <pre className="mt-3 max-h-[260px] overflow-auto whitespace-pre-wrap rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3 font-mono text-[11.5px] leading-[1.55] text-[var(--ink)]/85">
        {prompt}
      </pre>
      <div className="mt-3 flex justify-end">
        <button type="button" onClick={handleCopy} className="btn btn-ghost text-[11px]">
          {copied ? t`copied` : t`copy prompt`}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </article>
  );
}
